/**
 * URLs for the framed image permalink (`/media/{imagePublicId}`) — the on-domain wrapper page that
 * shows a Seavien-hosted image inside site chrome instead of dumping the viewer on the raw file.
 *
 * <p>One module so every link the app emits shares the backend's hint vocabulary. The `?in=` hint is a
 * CLAIM, not a fact: the server re-validates it against live content for that viewer on every render and
 * silently falls back to the unattributed frame if it does not hold. Emitting a wrong hint therefore
 * cannot make the page assert anything false — it just loses the footer.
 *
 * <p><b>This module used to be the feature gate</b> (AGENTS.md rule 34) and no longer needs to be.
 * `MediaPermalinkController` was `@ConditionalOnProperty` on `seavien.media-permalink.enabled`, so where
 * the flag was off the route did not exist and any href pointing at it rendered the 404 page — which is
 * exactly what shipped to production on 2026-07-26, turning every Seavien-hosted image in every
 * discussion body into a link to a 404. The root cause was not the missing gate but the split: the route
 * was held dark waiting on prod WAF rules nobody had written. Those rules now exist and the flag is gone,
 * so the route is unconditional and there is nothing left to gate.
 *
 * <p>Still build every link through this module — it owns the `?in=` encoding, and it is where a gate
 * would go again if `/media/` ever became conditional.
 *
 * Design: designs/01-now/2026-07-25-framed-image-permalink-media-page-design.md §5.1, §9.4
 * Retirement: designs/01-now/2026-07-26-feature-gating-simplification-and-stage-convergence-design.md
 */

/** Mirrors the backend `MediaContentKind` hint prefixes. */
export type MediaPermalinkHintKind = "d" | "t" | "s" | "g" | "u";

export interface MediaPermalinkHint {
    kind: MediaPermalinkHintKind;
    /** The owning content's public id (a username for `u`). */
    contentId: string;
}

/**
 * `/media/{imagePublicId}`, plus `?in={kind}-{contentId}` when the caller knows where the image lives.
 *
 * Returns `null` when there is no usable image id, so callers can decide not to render a link at all.
 *
 * The environment check that used to live here (`MetaHandler.isMediaPermalinkEnabled()`) was removed on
 * 2026-07-26 along with the `seavien.media-permalink.enabled` flag: the route is now unconditional in
 * every environment and the prod WAF allows `/media/`, so there is no state in which a link here would
 * 404. Keep reading the URL through this helper anyway — it is the one place the `?in=` hint is encoded.
 */
export function mediaPermalink(imagePublicId?: string | null, hint?: MediaPermalinkHint | null): string | null {
    const publicId = imagePublicId?.trim();
    if (!publicId) {
        return null;
    }

    const base = `/media/${encodeURIComponent(publicId)}`;
    const contentId = hint?.contentId?.trim();
    if (!hint || !contentId) {
        return base;
    }

    return `${base}?in=${encodeURIComponent(`${hint.kind}-${contentId}`)}`;
}
